import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  message,
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { relatorioService, unidadeService } from '../services/api';

const DEFAULT_PAGE_SIZE = 50;

const RelatoriosOperacionais = () => {
  const [activeTab, setActiveTab] = useState('alunos');
  const [loading, setLoading] = useState({
    filtros: false,
    alunos: false,
    turmas: false,
    matriculas: false,
    professores: false,
    exportacao: false,
  });
  const [errorMsg, setErrorMsg] = useState('');

  const [unidades, setUnidades] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: dayjs().startOf('month'),
    data_fim: dayjs(),
    ano_letivo: null,
    id_unidade: null,
  });

  const [alunosRows, setAlunosRows] = useState([]);
  const [turmasRows, setTurmasRows] = useState([]);
  const [matriculasRows, setMatriculasRows] = useState([]);
  const [professoresRows, setProfessoresRows] = useState([]);

  const [alunosPagination, setAlunosPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, total_pages: 0 });
  const [turmasPagination, setTurmasPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, total_pages: 0 });
  const [matriculasPagination, setMatriculasPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, total_pages: 0 });
  const [professoresPagination, setProfessoresPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, total_pages: 0 });

  const getCommonParams = useCallback(
    (page, pageSize) => {
      const dataInicio = filtros.data_inicio ? dayjs(filtros.data_inicio).format('YYYY-MM-DD') : null;
      const dataFim = filtros.data_fim ? dayjs(filtros.data_fim).format('YYYY-MM-DD') : null;

      const params = {
        data_referencia: dataFim || dayjs().format('YYYY-MM-DD'),
        data_inicio: dataInicio,
        data_fim: dataFim,
        page,
        page_size: pageSize,
      };
      if (filtros.ano_letivo) {
        params.ano_letivo = filtros.ano_letivo;
      }
      if (filtros.id_unidade) {
        params.id_unidade = filtros.id_unidade;
      }
      return params;
    },
    [filtros]
  );

  const loadUnidades = useCallback(async () => {
    setLoading((prev) => ({ ...prev, filtros: true }));
    try {
      const response = await unidadeService.list();
      setUnidades(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Erro ao carregar unidades para filtros.');
    } finally {
      setLoading((prev) => ({ ...prev, filtros: false }));
    }
  }, []);

  const loadAlunos = useCallback(
    async (page = 1, pageSize = alunosPagination.page_size) => {
      setLoading((prev) => ({ ...prev, alunos: true }));
      try {
        const response = await relatorioService.getRelatorioAlunosAtivos(getCommonParams(page, pageSize));
        const payload = response?.data || {};
        setAlunosRows(Array.isArray(payload.items) ? payload.items : []);
        setAlunosPagination(
          payload.pagination || { page, page_size: pageSize, total: 0, total_pages: 0 }
        );
        setErrorMsg('');
      } catch (error) {
        setErrorMsg('Erro ao carregar relatório de Alunos Ativos.');
      } finally {
        setLoading((prev) => ({ ...prev, alunos: false }));
      }
    },
    [alunosPagination.page_size, getCommonParams]
  );

  const loadTurmas = useCallback(
    async (page = 1, pageSize = turmasPagination.page_size) => {
      setLoading((prev) => ({ ...prev, turmas: true }));
      try {
        const response = await relatorioService.getRelatorioTurmasAbertas(getCommonParams(page, pageSize));
        const payload = response?.data || {};
        setTurmasRows(Array.isArray(payload.items) ? payload.items : []);
        setTurmasPagination(
          payload.pagination || { page, page_size: pageSize, total: 0, total_pages: 0 }
        );
        setErrorMsg('');
      } catch (error) {
        setErrorMsg('Erro ao carregar relatório de Turmas Abertas.');
      } finally {
        setLoading((prev) => ({ ...prev, turmas: false }));
      }
    },
    [turmasPagination.page_size, getCommonParams]
  );

  const loadMatriculas = useCallback(
    async (page = 1, pageSize = matriculasPagination.page_size) => {
      setLoading((prev) => ({ ...prev, matriculas: true }));
      try {
        const response = await relatorioService.getRelatorioMatriculasAtivas(getCommonParams(page, pageSize));
        const payload = response?.data || {};
        setMatriculasRows(Array.isArray(payload.items) ? payload.items : []);
        setMatriculasPagination(
          payload.pagination || { page, page_size: pageSize, total: 0, total_pages: 0 }
        );
        setErrorMsg('');
      } catch (error) {
        setErrorMsg('Erro ao carregar relatório de Matrículas Ativas.');
      } finally {
        setLoading((prev) => ({ ...prev, matriculas: false }));
      }
    },
    [matriculasPagination.page_size, getCommonParams]
  );

  const loadProfessores = useCallback(
    async (page = 1, pageSize = professoresPagination.page_size) => {
      setLoading((prev) => ({ ...prev, professores: true }));
      try {
        const response = await relatorioService.getRelatorioProfessoresSimples(getCommonParams(page, pageSize));
        const payload = response?.data || {};
        setProfessoresRows(Array.isArray(payload.items) ? payload.items : []);
        setProfessoresPagination(
          payload.pagination || { page, page_size: pageSize, total: 0, total_pages: 0 }
        );
        setErrorMsg('');
      } catch (error) {
        setErrorMsg('Erro ao carregar relatório de Professores Simples.');
      } finally {
        setLoading((prev) => ({ ...prev, professores: false }));
      }
    },
    [professoresPagination.page_size, getCommonParams]
  );

  const loadAll = useCallback(async () => {
    await Promise.all([
      loadAlunos(1, alunosPagination.page_size),
      loadTurmas(1, turmasPagination.page_size),
      loadMatriculas(1, matriculasPagination.page_size),
      loadProfessores(1, professoresPagination.page_size),
    ]);
  }, [
    alunosPagination.page_size,
    loadAlunos,
    loadProfessores,
    loadMatriculas,
    loadTurmas,
    matriculasPagination.page_size,
    professoresPagination.page_size,
    turmasPagination.page_size,
  ]);

  useEffect(() => {
    loadUnidades();
  }, [loadUnidades]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const alunosColumns = useMemo(
    () => [
      { title: 'Aluno', dataIndex: 'nome_aluno', key: 'nome_aluno' },
      { title: 'Responsável Financeiro', dataIndex: 'responsavel_financeiro', key: 'responsavel_financeiro' },
      { title: 'Unidade', dataIndex: 'unidade', key: 'unidade' },
      { title: 'Turma', dataIndex: 'turmas', key: 'turmas' },
    ],
    []
  );

  const turmasColumns = useMemo(
    () => [
      { title: 'Turma', dataIndex: 'nome_turma', key: 'nome_turma' },
      { title: 'Unidade', dataIndex: 'unidade', key: 'unidade' },
      { title: 'Professor', dataIndex: 'professor', key: 'professor' },
      { title: 'Alunos Matriculados', dataIndex: 'alunos_matriculados', key: 'alunos_matriculados', align: 'center' },
    ],
    []
  );

  const matriculasColumns = useMemo(
    () => [
      { title: 'Aluno', dataIndex: 'aluno', key: 'aluno' },
      { title: 'Unidade', dataIndex: 'unidade', key: 'unidade' },
      { title: 'Turma', dataIndex: 'turma', key: 'turma' },
      { title: 'Ano Letivo', dataIndex: 'ano_letivo', key: 'ano_letivo', align: 'center' },
    ],
    []
  );

  const professoresColumns = useMemo(
    () => [
      { title: 'Nome', dataIndex: 'nome', key: 'nome' },
      { title: 'Unidade', dataIndex: 'unidade', key: 'unidade' },
      { title: 'Modalidade', dataIndex: 'modalidade', key: 'modalidade' },
      {
        title: 'Quantidade de Turmas',
        dataIndex: 'quantidade_turmas',
        key: 'quantidade_turmas',
        align: 'center',
      },
      { title: 'Status', dataIndex: 'status', key: 'status', align: 'center' },
    ],
    []
  );

  const buildCsvContent = (headers, rows) => {
    const escapeCsv = (value) => {
      const text = String(value ?? '');
      return `"${text.replace(/"/g, '""')}"`;
    };

    const headerLine = headers.map((header) => escapeCsv(header.label)).join(';');
    const rowLines = rows.map((row) => headers.map((header) => escapeCsv(row[header.key])).join(';'));

    return `\ufeff${[headerLine, ...rowLines].join('\n')}`;
  };

  const exportarCsv = async () => {
    const endpointByTab = {
      alunos: {
        service: relatorioService.getRelatorioAlunosAtivos,
        fileName: 'alunos-ativos',
        headers: [
          { key: 'nome_aluno', label: 'Aluno' },
          { key: 'responsavel_financeiro', label: 'Responsável Financeiro' },
          { key: 'unidade', label: 'Unidade' },
          { key: 'turmas', label: 'Turma' },
        ],
      },
      turmas: {
        service: relatorioService.getRelatorioTurmasAbertas,
        fileName: 'turmas-abertas',
        headers: [
          { key: 'nome_turma', label: 'Turma' },
          { key: 'unidade', label: 'Unidade' },
          { key: 'professor', label: 'Professor' },
          { key: 'alunos_matriculados', label: 'Alunos Matriculados' },
        ],
      },
      matriculas: {
        service: relatorioService.getRelatorioMatriculasAtivas,
        fileName: 'matriculas-ativas',
        headers: [
          { key: 'aluno', label: 'Aluno' },
          { key: 'unidade', label: 'Unidade' },
          { key: 'turma', label: 'Turma' },
          { key: 'ano_letivo', label: 'Ano Letivo' },
        ],
      },
      professores: {
        service: relatorioService.getRelatorioProfessoresSimples,
        fileName: 'professores-simples',
        headers: [
          { key: 'nome', label: 'Nome' },
          { key: 'unidade', label: 'Unidade' },
          { key: 'modalidade', label: 'Modalidade' },
          { key: 'quantidade_turmas', label: 'Quantidade de Turmas' },
          { key: 'status', label: 'Status' },
        ],
      },
    };

    const target = endpointByTab[activeTab];
    if (!target) {
      message.error('Aba de relatório inválida para exportação.');
      return;
    }

    setLoading((prev) => ({ ...prev, exportacao: true }));
    try {
      const firstPage = await target.service(getCommonParams(1, DEFAULT_PAGE_SIZE));
      const firstPayload = firstPage?.data || {};
      const totalPages = Number(firstPayload?.pagination?.total_pages || 0);
      const pageSize = Number(firstPayload?.pagination?.page_size || DEFAULT_PAGE_SIZE);

      let allItems = Array.isArray(firstPayload?.items) ? [...firstPayload.items] : [];

      for (let page = 2; page <= totalPages; page += 1) {
        const response = await target.service(getCommonParams(page, pageSize));
        const payload = response?.data || {};
        const pageItems = Array.isArray(payload.items) ? payload.items : [];
        allItems = allItems.concat(pageItems);
      }

      const csvContent = buildCsvContent(target.headers, allItems);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${target.fileName}-${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success(`CSV exportado com ${allItems.length} registros.`);
    } catch (error) {
      message.error('Erro ao exportar CSV do relatório ativo.');
    } finally {
      setLoading((prev) => ({ ...prev, exportacao: false }));
    }
  };

  return (
    <Card title="Relatórios Operacionais">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {errorMsg ? <Alert type="error" message={errorMsg} showIcon /> : null}

        <Row gutter={12} align="bottom">
          <Col xs={24} sm={12} md={6}>
            <label>Data inicial</label>
            <DatePicker
              value={filtros.data_inicio}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              onChange={(value) => setFiltros((prev) => ({ ...prev, data_inicio: value || dayjs().startOf('month') }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label>Data final</label>
            <DatePicker
              value={filtros.data_fim}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              onChange={(value) => setFiltros((prev) => ({ ...prev, data_fim: value || dayjs() }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <label>Ano letivo</label>
            <InputNumber
              min={2000}
              max={2100}
              placeholder="Opcional"
              style={{ width: '100%' }}
              value={filtros.ano_letivo}
              onChange={(value) => setFiltros((prev) => ({ ...prev, ano_letivo: value || null }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <label>Unidade</label>
            <Select
              allowClear
              placeholder="Todas as unidades"
              value={filtros.id_unidade}
              style={{ width: '100%' }}
              loading={loading.filtros}
              onChange={(value) => setFiltros((prev) => ({ ...prev, id_unidade: value || null }))}
              options={unidades.map((u) => ({ label: u.nome_fantasia || u.nome, value: u.id }))}
            />
          </Col>
          <Col xs={24} sm={24} md={4}>
            <Space>
              <Button icon={<SearchOutlined />} type="primary" onClick={loadAll}>
                Atualizar
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportarCsv}
                loading={loading.exportacao}
              >
                Exportar CSV
              </Button>
            </Space>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'alunos',
              label: 'Alunos Ativos',
              children: (
                <Table
                  rowKey={(record) => `${record.nome_aluno}-${record.turmas}`}
                  columns={alunosColumns}
                  dataSource={alunosRows}
                  loading={loading.alunos}
                  locale={{ emptyText: <Empty description="Nenhum aluno ativo encontrado." /> }}
                  pagination={{
                    current: alunosPagination.page,
                    pageSize: alunosPagination.page_size,
                    total: alunosPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                  }}
                  onChange={(pagination) => loadAlunos(pagination.current, pagination.pageSize)}
                />
              ),
            },
            {
              key: 'turmas',
              label: 'Turmas Abertas',
              children: (
                <Table
                  rowKey={(record) => `${record.nome_turma}-${record.professor}`}
                  columns={turmasColumns}
                  dataSource={turmasRows}
                  loading={loading.turmas}
                  locale={{ emptyText: <Empty description="Nenhuma turma aberta encontrada." /> }}
                  pagination={{
                    current: turmasPagination.page,
                    pageSize: turmasPagination.page_size,
                    total: turmasPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                  }}
                  onChange={(pagination) => loadTurmas(pagination.current, pagination.pageSize)}
                />
              ),
            },
            {
              key: 'matriculas',
              label: 'Matrículas Ativas',
              children: (
                <Table
                  rowKey={(record) => `${record.aluno}-${record.turma}`}
                  columns={matriculasColumns}
                  dataSource={matriculasRows}
                  loading={loading.matriculas}
                  locale={{ emptyText: <Empty description="Nenhuma matrícula ativa encontrada." /> }}
                  pagination={{
                    current: matriculasPagination.page,
                    pageSize: matriculasPagination.page_size,
                    total: matriculasPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                  }}
                  onChange={(pagination) => loadMatriculas(pagination.current, pagination.pageSize)}
                />
              ),
            },
            {
              key: 'professores',
              label: 'Professores Simples',
              children: (
                <Table
                  rowKey={(record) => `${record.nome}-${record.unidade}-${record.modalidade}`}
                  columns={professoresColumns}
                  dataSource={professoresRows}
                  loading={loading.professores}
                  locale={{ emptyText: <Empty description="Nenhum professor encontrado." /> }}
                  pagination={{
                    current: professoresPagination.page,
                    pageSize: professoresPagination.page_size,
                    total: professoresPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                  }}
                  onChange={(pagination) => loadProfessores(pagination.current, pagination.pageSize)}
                />
              ),
            },
          ]}
        />
      </Space>
    </Card>
  );
};

export default RelatoriosOperacionais;
